import { uniq } from 'lodash';

import { BASE_URLS } from '@blockworks/platform/api';
import { STUDIO_QUERIES_ENDPOINTS } from '@blockworks/platform/api/research/studio/queries';
import {
    StudioVisualizationConfig,
    StudioVisualizationType,
} from '@blockworks/platform/api/research/studio/visualizations';
import { openNewTab } from '@blockworks/platform/utils';

import { getExpressionKeys } from '../visualization/workflows/utils';

import { ExportExecutionFormat, ExportExecutionResultsProps } from './export-execution-results.model';

const getExportUrl = (format: ExportExecutionFormat, visualization: ExportExecutionResultsProps['visualization']) => {
    let columns: string[] = [];

    if (visualization) {
        if (visualization.type === StudioVisualizationType.Table) {
            const config = visualization.config as StudioVisualizationConfig<StudioVisualizationType.Table>;
            if (config.options.groups?.length) {
                columns = uniq(config.options.groups.flatMap(group => group?.columns?.map?.(column => column.key)));
            } else {
                columns = [];
            }
        }
        if (visualization.type === StudioVisualizationType.Timeseries) {
            const config = visualization.config as StudioVisualizationConfig<StudioVisualizationType.Timeseries>;
            if (!config.options.xAxis.groupBy) {
                columns = uniq([
                    ...config.options.groups.flatMap(group =>
                        group.series.flatMap(series => {
                            if (series.computed) {
                                return getExpressionKeys(series.expression);
                            }

                            return series.column;
                        }),
                    ),
                    config.options.xAxis.column,
                ]);
            }
        }
    }

    let params = { format } as Record<string, string>;

    if (visualization?.execution?.executionId) {
        params = { ...params, executionId: visualization?.execution.executionId };
    }
    if (columns.length > 0) {
        params = { ...params, columns: columns.join(',') };
    }

    const paramString = new URLSearchParams(params).toString();
    if (!visualization?.queryId) {
        console.error('Query ID invalid.', { queryId: visualization?.queryId });
        return '';
    }
    return `${BASE_URLS['research-api']}${STUDIO_QUERIES_ENDPOINTS.GET.EXPORT_QUERY({
        id: visualization.queryId,
    })}?${paramString}`;
};

const makeExportClickHandler =
    ({
        format,
        visualization,
        onClick,
    }: ExportExecutionResultsProps & {
        format: ExportExecutionFormat;
        onClick?: (format: ExportExecutionFormat) => void;
    }): React.MouseEventHandler<HTMLAnchorElement> =>
    e => {
        e.preventDefault();
        onClick?.(format);
        openNewTab(getExportUrl(format, visualization));
    };

export { getExportUrl, makeExportClickHandler };
